// src/contexts/AuthContext.tsx
import React, { createContext, useContext, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import { JwtPayloadSchema } from '@/types/jwt-schema';
import { logger } from '@/utils/logger';

interface Permissoes {
  [modulo: string]: { [acao: string]: boolean } | undefined;
}

interface AuthContextType {
  isAuthenticated: boolean;
  permissoes: Permissoes | null;
  role: string | null;
  userId: number | null; // Adicionado para extrair ID do usuário do JWT
  nome: string | null;
  /** Nome completo (claim `nomeCompleto`). Cai em `nome` quando o token e
   *  anterior ao deploy que introduziu a claim — ver jwt-schema.ts. */
  nomeCompleto: string | null;
  userName: string | null;
  penultimoAcesso: string | null;
  isInitialized: boolean;
  login: (token: string) => void;
  logout: () => void;
  hasPermission: (permission: string) => boolean; // verifica permissões
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const initialToken = sessionStorage.getItem('jwtToken');
  let initialPermissoes: Permissoes | null = null;
  let initialRole: string | null = null;
  let initialUserId: number | null = null; // Adicionado para inicialização síncrona
  let initialNome: string | null = null;
  let initialNomeCompleto: string | null = null;
  let initialUserName: string | null = null;
  let initialPenultimoAcesso: string | null = null;
  let initialIsAuthenticated = !!initialToken;
  let initialIsInitialized = false;

  // Inicialização síncrona do estado
  if (initialToken) {
    try {
      const decoded = JwtPayloadSchema.parse(jwtDecode(initialToken));
      initialPermissoes = decoded.permissoes || {};
      initialRole = decoded.role || null;
      initialUserId = decoded.id ? Number(decoded.id) : null; // Extraído do JWT
      initialNome = decoded.nome ?? null;
      // `||` e nao `??`: cobre string vazia alem de undefined.
      initialNomeCompleto = decoded.nomeCompleto || decoded.nome || null;
      initialUserName = decoded.sub ?? null;
      initialPenultimoAcesso = decoded.penultimo_acesso ?? null;
      initialIsInitialized = true;
    } catch (error) {
      logger.error('Erro ao decodificar token na inicialização:', error);
      initialPermissoes = null;
      initialRole = null;
      initialUserId = null;
      initialNome = null;
      initialNomeCompleto = null;
      initialUserName = null;
      initialPenultimoAcesso = null;
      initialIsAuthenticated = false;
      initialIsInitialized = true;
      sessionStorage.removeItem('jwtToken');
      sessionStorage.removeItem('permissoes');
      sessionStorage.removeItem('role');
    }
  } else {
    initialIsInitialized = true;
  }

  const [isAuthenticated, setIsAuthenticated] = useState(initialIsAuthenticated);
  const [permissoes, setPermissoes] = useState<Permissoes | null>(initialPermissoes);
  const [role, setRole] = useState<string | null>(initialRole);
  const [userId, setUserId] = useState<number | null>(initialUserId);
  const [nome, setNome] = useState<string | null>(initialNome);
  const [nomeCompleto, setNomeCompleto] = useState<string | null>(initialNomeCompleto);
  const [userName, setUserName] = useState<string | null>(initialUserName);
  const [penultimoAcesso, setPenultimoAcesso] = useState<string | null>(initialPenultimoAcesso);
  const [isInitialized, setIsInitialized] = useState(initialIsInitialized);

  const updateAuthState = (token: string) => {
    try {
      const decoded = JwtPayloadSchema.parse(jwtDecode(token));
      sessionStorage.setItem('jwtToken', token);
      sessionStorage.setItem('permissoes', JSON.stringify(decoded.permissoes || {}));
      sessionStorage.setItem('role', decoded.role || '');
      setPermissoes(decoded.permissoes || {});
      setRole(decoded.role || null);
      setUserId(decoded.id ? Number(decoded.id) : null); // Extraído do JWT
      setNome(decoded.nome ?? null);
      setNomeCompleto(decoded.nomeCompleto || decoded.nome || null);
      setUserName(decoded.sub ?? null);
      setPenultimoAcesso(decoded.penultimo_acesso ?? null);
      setIsAuthenticated(true);
      setIsInitialized(true);
    } catch (error) {
      logger.error('Erro ao decodificar token:', error);
      setPermissoes(null);
      setRole(null);
      setUserId(null);
      setNome(null);
      setNomeCompleto(null);
      setUserName(null);
      setPenultimoAcesso(null);
      setIsAuthenticated(false);
      setIsInitialized(true);
      sessionStorage.removeItem('jwtToken');
      sessionStorage.removeItem('permissoes');
      sessionStorage.removeItem('role');
    }
  };

  // Removido useEffect inicial, pois a inicialização agora é síncrona
  const login = (token: string) => {
    updateAuthState(token);
  };

  const logout = () => {
    sessionStorage.removeItem('jwtToken');
    sessionStorage.removeItem('permissoes');
    sessionStorage.removeItem('role');
    setIsAuthenticated(false);
    setPermissoes(null);
    setRole(null);
    setUserId(null);
    setNome(null);
    setNomeCompleto(null);
    setUserName(null);
    setPenultimoAcesso(null);
    setIsInitialized(true);
    window.location.href = '/login';
  };

  const hasPermission = (permission: string): boolean => {
    if (role === 'ROLE_MASTER') return true;
    const [modulo, chave] = permission.split('.');
    return permissoes?.[modulo]?.[chave] === true;
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, permissoes, role, userId, nome, nomeCompleto, userName, penultimoAcesso, isInitialized, login, logout, hasPermission }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};
